// Copyright ©️ 2026 eVolve MEP, LLC

import { type ComponentProps, useEffect, useMemo, useState } from 'react';

import { type TaskType, type TaskTypeGroupId, useEvolvePaginatedGet } from '@evolvemep/api-ts';
import { ActionIcon, ScrollArea, Stack, Text, Group } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { TextInputDebounced } from 'components/Mantine/TextInputDebounced';
import { TriggerOnVisible } from 'hooks/useOnScreen';
import type { ManageTaskGroupsModal } from 'modules/Shop/Tasks/TaskTypes/ManageTaskGroupsModal';
import { useTaskTypeModals } from 'modules/Shop/Tasks/TaskTypes/modalRegistries';
import { TaskGroupRow } from 'modules/Shop/Tasks/TaskTypes/TaskGroupRow';
import type { TaskGroupFilter } from 'modules/Shop/Tasks/TaskTypes/types';
import { UngroupedTaskGroupRow } from 'modules/Shop/Tasks/TaskTypes/UngroupedTaskGroupRow';

type TaskGroupPanelProps = {
  selectedTasks: TaskType[];
  groupFilter: TaskGroupFilter;
  onGroupFilterChange: (filter: TaskGroupFilter) => void;
} & Pick<ComponentProps<typeof ManageTaskGroupsModal>, 'onTaskGroupUpdated' | 'onTaskGroupDeleted'>;

export const TaskGroupPanel = ({
  selectedTasks,
  groupFilter,
  onGroupFilterChange,
  onTaskGroupUpdated,
  onTaskGroupDeleted,
}: TaskGroupPanelProps) => {
  const { openModal } = useTaskTypeModals();
  const [groupSearch, setGroupSearch] = useState('');

  const groupsDefaultOpts = useMemo(
    () => ({
      defaultConfig: {
        params: {
          name: encodeURIComponent(groupSearch),
        },
      },
    }),
    [groupSearch],
  );

  const {
    setDefaultOpts: setGroupsDefaultOpts,
    reset: refreshGroups,
    data: groups,
    loading: groupsIsLoading,
    fetchNextPage: fetchNextPageOfGroups,
    entireCount: groupsEntireCount,
  } = useEvolvePaginatedGet('shop/taskTypeGroup', groupsDefaultOpts);

  useEffect(() => setGroupsDefaultOpts(groupsDefaultOpts), [groupsDefaultOpts, setGroupsDefaultOpts]);

  const selectionCountsByGroup = useMemo(
    () =>
      selectedTasks.reduce((map, { taskTypeGroupId }) => {
        // null taskTypeGroupId means ungrouped
        const key = taskTypeGroupId ?? null;
        map.set(key, (map.get(key) ?? 0) + 1);
        return map;
      }, new Map<TaskTypeGroupId | null, number>()),
    [selectedTasks],
  );

  return (
    <Stack w={260} p="md" gap="xs" style={{ flexShrink: 0, overflow: 'hidden' }}>
      <Text fw={600}>Groups</Text>
      <Group gap="xs" align="center">
        <TextInputDebounced
          flex={1}
          placeholder="Search groups"
          leftSection={<EvolveIcon icon="Search" />}
          value={groupSearch}
          onChange={setGroupSearch}
        />
        <AgGridStyleTooltip label="Manage task groups" withArrow>
          <ActionIcon
            size="input-sm"
            variant="light"
            onClick={() =>
              openModal('ManageTaskGroupsModal', {
                onTaskGroupCreated: () => refreshGroups(),
                onTaskGroupUpdated: (updated) => {
                  refreshGroups();
                  onTaskGroupUpdated(updated);
                },
                onTaskGroupDeleted: (deleted) => {
                  refreshGroups();
                  onTaskGroupDeleted(deleted);
                },
              })
            }
          >
            <EvolveIcon icon="FolderEdit" />
          </ActionIcon>
        </AgGridStyleTooltip>
      </Group>

      <ScrollArea flex={1}>
        <Stack gap={0}>
          <TaskGroupRow label="All Tasks" selected={groupFilter === 'all'} onClick={() => onGroupFilterChange('all')} />

          {groups.map((g) => (
            <TaskGroupRow
              key={g.taskTypeGroupId}
              label={g.name}
              selected={typeof groupFilter === 'object' && groupFilter.taskTypeGroupId === g.taskTypeGroupId}
              selectionCount={selectionCountsByGroup.get(g.taskTypeGroupId) ?? 0}
              onClick={() => onGroupFilterChange(g)}
              totalCount={g.taskTypesInGroupCount}
            />
          ))}

          {!groupsIsLoading && groups.length === 0 && groupSearch && (
            <Text fz="xs" c="dimmed" px="md" py="xs">
              No task groups match your search.
            </Text>
          )}

          <TriggerOnVisible onVisible={fetchNextPageOfGroups} loading={groupsIsLoading} />

          {groups.length === groupsEntireCount && (
            <UngroupedTaskGroupRow
              selected={groupFilter === 'ungrouped'}
              selectionCount={selectionCountsByGroup.get(null) ?? 0}
              onClick={() => onGroupFilterChange('ungrouped')}
            />
          )}
        </Stack>
      </ScrollArea>
    </Stack>
  );
};
